import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowRight,
  Plant,
  Cow,
  Drop,
  Factory,
  Globe,
  Leaf,
  ForkKnife,
  Buildings,
  Boat,
  Bank,
  ShieldCheck,
  Flask,
} from "@phosphor-icons/react";
import {
  PILLARS,
  PILLAR_STATS,
  INTEGRATED_MODEL,
  PRODUCTS,
  INDUSTRIES,
  CERTS,
  DIFFERENTIATORS,
  IMAGES,
} from "../data";
import { motion, AnimatePresence } from "framer-motion";

const STEP_ICONS = [Plant, Leaf, Cow, Drop, Factory, Globe];

const INDUSTRY_ICONS = {
  ForkKnife,
  Buildings,
  Factory,
  Boat,
  Bank,
  Flask,
  Cow,
};

const PILLAR_ICONS = { Drop, Leaf, Cow };

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <ThreePillars />
      <StatsBar />
      <WhoWeAre />
      <IntegratedModel />
      <QualityPromise />
      <ProductsOverview />
      <WhyChooseUs />
      <IndustriesServed />
      <Certifications />
      <ContactCTA />
    </div>
  );
}

function Hero() {
  const [activePillar, setActivePillar] = useState(0); // 0: dairy, 1: pharma, 2: cattle
  const [paused, setPaused] = useState(false);
  const AUTO_INTERVAL = 4000;
  
  const PILLAR_DATA = [
    {
      id: "dairy",
      label: "PILLAR 01 · DAIRY MANUFACTURING",
      headline: "Premium Dairy.",
      subtext: "Paneer · Ghee · Cheese · Butter · Export Ready",
      image: "https://images.unsplash.com/photo-1631452180519-c014fe946bc7?w=1600&q=85",
      imageAlt: "Premium dairy ingredient — AxonPremium",
      bgClass: "bg-bone",
    },
    {
      id: "pharma",
      label: "PILLAR 02 · PHARMA CROP EXPORT",
      headline: "Pharma Crops.",
      subtext: "Moringa · Ashwagandha · Curcumin · 17+ Crops",
      image: IMAGES.moringa,
      imageAlt: "Pharmaceutical-grade Moringa cultivation",
      bgClass: "bg-[#F3F1E8]",
    },
    {
      id: "cattle",
      label: "PILLAR 03 · CATTLE BREEDING",
      headline: "Bred Cattle.",
      subtext: "Girlando Cows · Premium Buffalo · Institutional Lots",
      image: IMAGES.cattle,
      imageAlt: "Premium Girlando crossbreed cattle",
      bgClass: "bg-[#EFEDE3]",
    },
  ];

  const active = PILLAR_DATA[activePillar];

  // Auto-advance carousel
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => {
      setActivePillar((curr) => (curr + 1) % PILLAR_DATA.length);
    }, AUTO_INTERVAL);
    return () => clearInterval(t);
  }, [paused, activePillar, PILLAR_DATA.length]);

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset, velocity) => {
    return Math.abs(offset) * velocity;
  };

  const paginate = (newDirection) => {
    const next = (activePillar + newDirection + PILLAR_DATA.length) % PILLAR_DATA.length;
    setActivePillar(next);
  };

  const trustBadges = [
    "FSSAI Aligned",
    "APEDA Registered",
    "ISO 22000",
    "Lot-Level Traceable",
    "100% In-House",
    "Export Ready",
  ];

  return (
    <section 
      className="relative overflow-hidden" 
      data-testid="hero-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* MOBILE HERO (Carousel) — below 1024px */}
      <div className="lg:hidden relative w-full h-[100svh] bg-olive-deep overflow-hidden">
        <AnimatePresence initial={false} custom={activePillar}>
          <motion.div
            key={activePillar}
            custom={activePillar}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="absolute inset-0 w-full h-full"
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={1}
            onDragEnd={(e, { offset, velocity }) => {
              const swipe = swipePower(offset.x, velocity.x);
              if (swipe < -swipeConfidenceThreshold) {
                paginate(1);
              } else if (swipe > swipeConfidenceThreshold) {
                paginate(-1);
              }
            }}
          >
            <img
              src={active.image}
              alt={active.imageAlt}
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
            
            {/* Content at Bottom */}
            <div className="absolute bottom-0 left-0 right-0 p-6 pb-20 flex flex-col gap-1">
              <span className="font-mono text-[11px] tracking-[0.15em] text-terracotta uppercase">
                {active.label}
              </span>
              <h1 className="font-heading text-[36px] text-white leading-tight mt-1">
                {active.headline}
              </h1>
              <p className="font-mono text-[11px] tracking-[0.1em] text-white/80 uppercase">
                {active.subtext}
              </p>
              <Link
                to="/contact"
                className="mt-4 w-full h-[48px] bg-[#C9A84C] text-[#1A1A1A] flex items-center justify-center font-medium uppercase tracking-widest text-[13px] transition-transform active:scale-[0.98]"
              >
                Partner With Us <ArrowRight size={16} className="ml-2" />
              </Link>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Carousel Dots */}
        <div className="absolute bottom-8 left-0 right-0 flex justify-center gap-2 z-20">
          {PILLAR_DATA.map((_, i) => (
            <button
              key={i}
              onClick={() => setActivePillar(i)}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === activePillar ? "bg-terracotta" : "bg-white"
              }`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      </div>

      {/* DESKTOP HERO (Split) — 1024px and above */}
      <div className="hidden lg:grid grid-cols-[55%_45%] min-h-screen">
        {/* Left Column */}
        <div className={`px-16 pt-32 pb-16 flex flex-col justify-between transition-colors duration-700 ${active.bgClass}`}>
          <div className="flex flex-col">
            <span className="overline text-terracotta fade-up-q">
              Vertically Integrated · Three Pillars · One Farm
            </span>

            <h1 className="mt-8 font-heading font-light tracking-tight leading-[0.95] text-olive-deep">
              <span className="block text-[4.2rem] xl:text-[4.6rem] fade-up-q">
                From one farm.
              </span>
              <span
                key={activePillar}
                className="block italic text-terracotta text-[4.2rem] xl:text-[4.6rem] mt-1 swap-fade"
              >
                {active.headline}
              </span>
            </h1>

            <p className="mt-7 max-w-xl text-base font-mono tracking-widest2 uppercase text-olive-soft">
              {active.subtext}
            </p>

            <div className="mt-10 flex gap-4 fade-up-q">
              <Link to="/contact" className="btn-primary">
                Partner With Us <ArrowUpRight size={14} weight="bold" />
              </Link>
              <Link to="/products" className="btn-outline">
                See Our Products <ArrowRight size={14} weight="bold" />
              </Link>
            </div>

            {/* Pillar Tabs */}
            <div className="mt-14 max-w-2xl">
              <div className="grid grid-cols-3 gap-5">
                {PILLAR_DATA.map((p, i) => {
                  const isActive = i === activePillar;
                  return (
                    <button
                      key={p.id}
                      onClick={() => setActivePillar(i)}
                      className="group text-left focus:outline-none"
                    >
                      <div className="relative h-[2px] bg-olive/15 overflow-hidden">
                        <div
                          className={`absolute inset-y-0 left-0 bg-terracotta transition-all duration-300 ${
                            isActive ? "w-full" : "w-0"
                          }`}
                        />
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <span className={`font-mono text-[0.62rem] tracking-widest2 ${isActive ? "text-terracotta" : "text-olive-soft/70"}`}>
                          0{i + 1}
                        </span>
                        <span className={`font-mono text-[0.62rem] tracking-widest2 uppercase truncate transition-colors ${isActive ? "text-olive-deep font-bold" : "text-olive-soft/70 group-hover:text-olive-deep"}`}>
                          {p.id}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Desktop Trust Badges — pinned to bottom */}
          <div className="mt-auto pt-12 flex flex-wrap items-center gap-x-8 gap-y-3 fade-up-q">
            {trustBadges.slice(0, 4).map((c, i) => (
              <span key={c} className="flex items-center gap-2 font-mono text-[0.62rem] tracking-widest2 uppercase text-olive-soft">
                <ShieldCheck size={14} weight="duotone" className="text-terracotta" />
                {c}
              </span>
            ))}
          </div>
        </div>

        {/* Right Column (Image) */}
        <div className="relative h-full overflow-hidden bg-sand">
          <AnimatePresence mode="wait">
            <motion.img
              key={activePillar}
              src={active.image}
              alt={active.imageAlt}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-t from-olive-deep/40 via-transparent to-transparent pointer-events-none" />
        </div>
      </div>

      {/* MOBILE Trust Strip — below carousel */}
      <div className="lg:hidden trust-strip" data-testid="mobile-trust-strip">
        <div className="trust-ticker-track">
          {[...trustBadges, ...trustBadges, ...trustBadges, ...trustBadges].map((t, i) => (
            <div key={i} className="trust-item">
              <span className="trust-dot" />
              {t}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ThreePillars() {
  return (
    <section
      className="bg-olive-deep text-bone py-24 lg:py-32 relative overflow-hidden"
      data-testid="three-pillars-section"
    >
      <div className="max-w-8xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16 lg:mb-20">
          <div>
            <span className="overline text-terracotta">02 — The Operation</span>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl mt-6 text-bone leading-[1.05] max-w-3xl">
              Three world-class businesses,{" "}
              <span className="italic">grown on one farm.</span>
            </h2>
          </div>
          <p className="max-w-sm text-bone/65 leading-relaxed">
            Every pillar runs on the same owned land, the same lot-level
            traceability, and the same export-grade discipline.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 lg:divide-x lg:divide-terracotta/40">
          {PILLARS.map((p, i) => {
            const Icon = PILLAR_ICONS[p.icon] || Drop;
            return (
              <div
                key={p.n}
                className={`py-10 lg:py-2 ${
                  i === 0 ? "lg:pr-12" : i === PILLARS.length - 1 ? "lg:pl-12" : "lg:px-12"
                } ${
                  i > 0 ? "border-t border-terracotta/30 lg:border-t-0" : ""
                }`}
                data-testid={`pillar-${p.n}`}
              >
                <div className="flex items-start justify-between">
                  <span className="font-mono text-xs tracking-widest2 uppercase text-terracotta">
                    Pillar {p.n}
                  </span>
                  <Icon
                    size={32}
                    weight="duotone"
                    className="text-bone/75"
                  />
                </div>
                <h3 className="font-heading text-3xl md:text-4xl mt-8 text-bone leading-tight">
                  {p.name}
                </h3>
                <p className="mt-5 text-bone/70 leading-relaxed text-sm lg:text-base max-w-md">
                  {p.desc}
                </p>
                <Link
                  to={`/products?tab=${p.tab}`}
                  className="mt-8 inline-flex items-center gap-2 font-mono text-[0.7rem] tracking-widest2 uppercase text-terracotta hover:text-bone transition-colors"
                  data-testid={`pillar-cta-${p.n}`}
                >
                  {p.cta}
                  <ArrowUpRight size={14} weight="bold" />
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function StatsBar() {
  return (
    <section
      className="bg-olive-deep text-bone py-20 lg:py-28 border-t border-bone/10"
      data-testid="stats-bar"
    >
      <div className="max-w-8xl mx-auto px-6 lg:px-12">
        <div className="mb-12 lg:mb-16 max-w-3xl">
          <span className="overline text-terracotta">03 — The Numbers</span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl mt-6 text-bone leading-tight">
            What vertical integration looks like,{" "}
            <span className="italic">in three numbers.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 lg:divide-x lg:divide-terracotta/30">
          {PILLAR_STATS.map((s, i) => (
            <div
              key={s.label}
              className={`py-10 lg:py-2 ${
                i === 0 ? "lg:pr-10" : i === PILLAR_STATS.length - 1 ? "lg:pl-10" : "lg:px-10"
              } ${i > 0 ? "border-t border-terracotta/25 lg:border-t-0" : ""}`}
              data-testid={`pillar-stat-${i}`}
            >
              <div
                className={`font-heading font-light tracking-tighter leading-[0.85] text-bone ${
                  s.accent
                    ? "text-[7rem] md:text-[10rem] lg:text-[13rem]"
                    : "text-[5.5rem] md:text-[8rem] lg:text-[11rem]"
                }`}
                data-testid={s.accent ? "stat-zero-headline" : `pillar-stat-value-${i}`}
              >
                {s.value}
              </div>
              <div className="mt-6 overline text-terracotta">{s.label}</div>
              <p className="mt-5 max-w-xs text-bone/70 leading-relaxed text-sm">
                {s.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhoWeAre() {
  return (
    <section
      className="max-w-8xl mx-auto px-6 lg:px-12 py-24 lg:py-36"
      data-testid="who-we-are-section"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-4">
          <span className="overline">04 — Who We Are</span>
        </div>
        <div className="lg:col-span-8">
          <h2 className="h-section text-4xl md:text-5xl lg:text-6xl">
            One farm. One supply chain.{" "}
            <span className="italic text-terracotta">Zero compromise.</span>
          </h2>
          <p className="mt-10 text-lg text-olive-soft leading-relaxed max-w-3xl">
            We are not a co-operative. We do not aggregate milk from thousands
            of fragmented farmers. AxonPremium owns the land, grows the feed,
            raises the cattle, and processes every batch in our own facility.
            That control is what allows us to guarantee batch-level
            consistency, traceability, and a quality spec that stands up to
            international export grade.
          </p>
        </div>
      </div>
    </section>
  );
}

function IntegratedModel() {
  return (
    <section
      className="bg-sand py-24 lg:py-36 relative overflow-hidden"
      data-testid="integrated-model-section"
    >
      <div className="max-w-8xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-20">
          <div>
            <span className="overline">05 — The Integrated Model</span>
            <h2 className="h-section text-4xl md:text-5xl lg:text-6xl mt-6 max-w-3xl">
              Land to logistics. Built end-to-end.
            </h2>
          </div>
          <p className="max-w-md text-olive-soft">
            Six closed-loop stages, all owned and operated under one roof. No
            outsourced milk. No third-party feed. No quality variance.
          </p>
        </div>

        {/* Desktop: horizontal flow timeline */}
        <div className="hidden lg:block">
          <div className="relative">
            {/* Connecting line */}
            <div className="absolute top-[3.25rem] left-[3%] right-[3%] step-line" />
            <div className="grid grid-cols-6 gap-6 relative">
              {INTEGRATED_MODEL.map((step, i) => {
                const Icon = STEP_ICONS[i] || Plant;
                return (
                  <div
                    key={step.title}
                    data-testid={`integrated-step-${step.step}`}
                    className="relative flex flex-col"
                  >
                    {/* Node */}
                    <div className="flex justify-center mb-10">
                      <div className="w-8 h-8 bg-sand border border-terracotta flex items-center justify-center">
                        <Icon size={16} weight="duotone" className="text-terracotta" />
                      </div>
                    </div>
                    <div className="font-mono text-[0.65rem] tracking-widest2 text-terracotta uppercase">
                      Step {step.step}
                    </div>
                    <h3 className="font-heading text-2xl xl:text-3xl mt-3 text-olive-deep leading-tight">
                      {step.title}
                    </h3>
                    <p className="mt-4 text-sm leading-relaxed text-olive-soft max-w-[18rem]">
                      {step.body}
                    </p>
                    {i < INTEGRATED_MODEL.length - 1 && (
                      <ArrowRight
                        size={14}
                        weight="bold"
                        className="absolute -right-3 top-[3rem] text-terracotta"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Journey caption */}
          <div className="mt-20 flex items-center justify-center gap-4 font-mono text-[0.72rem] uppercase tracking-widest2 text-olive-soft flex-wrap">
            {["Land", "Crop", "Cattle", "Milk", "Processing", "Your Product"].map(
              (label, i, arr) => (
                <React.Fragment key={label}>
                  <span className={i === arr.length - 1 ? "text-terracotta" : ""}>
                    {label}
                  </span>
                  {i < arr.length - 1 && (
                    <ArrowRight size={12} className="text-terracotta/50" />
                  )}
                </React.Fragment>
              )
            )}
          </div>
        </div>

        {/* Mobile / tablet: vertical timeline */}
        <div className="lg:hidden relative pl-10">
          <div className="absolute left-[1.05rem] top-2 bottom-2 w-px bg-terracotta/40" />
          <div className="space-y-12">
            {INTEGRATED_MODEL.map((step, i) => {
              const Icon = STEP_ICONS[i] || Plant;
              return (
                <div
                  key={step.title}
                  data-testid={`integrated-step-mobile-${step.step}`}
                  className="relative"
                >
                  <div className="absolute -left-[2.45rem] top-0 w-8 h-8 bg-sand border border-terracotta flex items-center justify-center">
                    <Icon size={16} weight="duotone" className="text-terracotta" />
                  </div>
                  <div className="font-mono text-[0.62rem] tracking-widest2 text-terracotta uppercase">
                    Step {step.step}
                  </div>
                  <h3 className="font-heading text-2xl mt-2 text-olive-deep">
                    {step.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-olive-soft">
                    {step.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function QualityPromise() {
  return (
    <section
      className="max-w-8xl mx-auto px-6 lg:px-12 py-24 lg:py-36"
      data-testid="quality-promise-section"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <span className="overline">06 — Quality Promise</span>
          <h2 className="h-section text-4xl md:text-5xl mt-6">
            Why our quality is{" "}
            <span className="italic text-terracotta">different.</span>
          </h2>
          <p className="mt-8 text-olive-soft leading-relaxed">
            Most dairy brands buy raw milk from thousands of unrelated farmers.
            That model is exposed to weather risk, feed inconsistency, and
            uneven quality. We replaced it.
          </p>
          <Link
            to="/sustainability"
            className="btn-outline mt-10"
            data-testid="quality-promise-cta"
          >
            How Moringa Powers Quality
            <ArrowRight size={14} weight="bold" />
          </Link>
        </div>

        <div className="lg:col-span-7">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-olive/10">
            <div className="bg-bone p-8" data-testid="industry-norm-block">
              <span className="overline text-olive-soft">Industry Norm</span>
              <ul className="mt-6 space-y-4 text-olive-soft text-sm">
                <li>· Milk aggregated from thousands of farmers</li>
                <li>· Feed quality varies between suppliers</li>
                <li>· Limited traceability to source farm</li>
                <li>· Inconsistent batch profile</li>
                <li>· Vulnerable to weather & monsoon shocks</li>
              </ul>
            </div>
            <div
              className="bg-olive text-bone p-8"
              data-testid="axon-model-block"
            >
              <span className="overline text-bone/70">The AxonPremium Model</span>
              <ul className="mt-6 space-y-4 text-bone/85 text-sm">
                <li className="flex gap-3">
                  <ShieldCheck size={18} className="shrink-0 mt-0.5 text-terracotta" weight="duotone" />
                  100% in-house milk from owned cattle
                </li>
                <li className="flex gap-3">
                  <ShieldCheck size={18} className="shrink-0 mt-0.5 text-terracotta" weight="duotone" />
                  Pharmaceutical-grade Moringa feed
                </li>
                <li className="flex gap-3">
                  <ShieldCheck size={18} className="shrink-0 mt-0.5 text-terracotta" weight="duotone" />
                  Lot-level traceability to farm block
                </li>
                <li className="flex gap-3">
                  <ShieldCheck size={18} className="shrink-0 mt-0.5 text-terracotta" weight="duotone" />
                  Consistent batch chemistry
                </li>
                <li className="flex gap-3">
                  <ShieldCheck size={18} className="shrink-0 mt-0.5 text-terracotta" weight="duotone" />
                  Closed-loop, weather-resilient supply
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProductsOverview() {
  return (
    <section
      className="max-w-8xl mx-auto px-6 lg:px-12 pb-24 lg:pb-36"
      data-testid="products-overview-section"
    >
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
        <div>
          <span className="overline">07 — Product Portfolio</span>
          <h2 className="h-section text-4xl md:text-5xl lg:text-6xl mt-6 max-w-2xl">
            Value-added dairy. Built for institutions.
          </h2>
        </div>
        <Link to="/products" className="btn-outline self-start" data-testid="products-overview-cta">
          See Full Catalog
          <ArrowRight size={14} weight="bold" />
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {PRODUCTS.slice(0, 9).map((p) => (
          <article
            key={p.name}
            className="card-flat group overflow-hidden"
            data-testid={`product-card-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="relative aspect-[4/3] overflow-hidden bg-sand">
              <img
                src={p.img}
                alt={p.name}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              {p.coming && (
                <span className="absolute top-4 left-4 badge-pill bg-bone/90">
                  Coming 2026
                </span>
              )}
              {p.flagship && (
                <span className="absolute top-4 left-4 badge-pill bg-terracotta text-[#1A1A1A] border-terracotta">
                  Flagship
                </span>
              )}
            </div>
            <div className="p-6">
              <div className="flex items-baseline justify-between gap-2">
                <h3 className="font-heading text-2xl text-olive-deep">
                  {p.name}
                </h3>
                <span className="overline text-[0.6rem]">{p.use}</span>
              </div>
              <p className="mt-3 text-sm text-olive-soft leading-relaxed">
                {p.desc}
              </p>
              <div className="mt-5 pt-5 border-t border-olive/10 text-[0.6rem] font-mono uppercase tracking-widest2 text-olive">
                100% In-House · Organic · Traceable
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function WhyChooseUs() {
  return (
    <section
      className="bg-olive-deep text-bone py-24 lg:py-36"
      data-testid="why-choose-us-section"
    >
      <div className="max-w-8xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <span className="overline">08 — Why AxonPremium</span>
          <h2 className="font-heading text-4xl md:text-5xl mt-6 text-bone">
            Built for the buyer who cannot accept variance.
          </h2>
        </div>
        <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-12">
          {DIFFERENTIATORS.map((d, i) => (
            <div
              key={d.title}
              className="border-t border-bone/20 pt-6"
              data-testid={`differentiator-${i}`}
            >
              <span className="font-mono text-xs text-terracotta tracking-widest2">
                0{i + 1}
              </span>
              <h3 className="font-heading text-2xl md:text-3xl mt-3">
                {d.title}
              </h3>
              <p className="mt-4 text-bone/70 leading-relaxed text-sm">
                {d.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function IndustriesServed() {
  return (
    <section
      className="max-w-8xl mx-auto px-6 lg:px-12 py-24 lg:py-36"
      data-testid="industries-section"
    >
      <div className="mb-14">
        <span className="overline">09 — Industries We Serve</span>
        <h2 className="h-section text-4xl md:text-5xl lg:text-6xl mt-6 max-w-3xl">
          Trusted across hospitality, manufacturing & global trade.
        </h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-olive/15">
        {INDUSTRIES.map((ind) => {
          const Icon = INDUSTRY_ICONS[ind.icon] || Buildings;
          const featured = ind.featured;
          return (
            <div
              key={ind.name}
              className={`group p-8 lg:p-10 flex flex-col gap-6 min-h-[300px] transition-colors duration-300 cursor-default ${
                featured
                  ? "bg-terracotta text-olive-deep hover:bg-terracotta-dark"
                  : "bg-bone text-olive-deep hover:bg-terracotta hover:text-olive-deep"
              }`}
              data-testid={`industry-${ind.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Icon
                size={36}
                weight="duotone"
                className={`${
                  featured
                    ? "text-olive-deep"
                    : "text-terracotta group-hover:text-olive-deep"
                } transition-colors`}
              />
              <div className="mt-auto">
                <h3
                  className={`font-heading text-2xl leading-tight ${
                    featured ? "text-olive-deep" : "text-olive-deep group-hover:text-olive-deep"
                  } transition-colors`}
                >
                  {ind.name}
                </h3>
                <p
                  className={`mt-3 text-sm leading-relaxed ${
                    featured
                      ? "text-olive-deep/80"
                      : "text-olive-soft group-hover:text-olive-deep/80"
                  } transition-colors`}
                >
                  {ind.desc}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Certifications() {
  return (
    <section
      className="border-t border-b border-olive/10 bg-bone py-12"
      data-testid="certifications-section"
    >
      <div className="max-w-8xl mx-auto px-6 lg:px-12 flex flex-col md:flex-row md:items-center justify-between gap-8">
        <span className="overline">Compliance & Certifications</span>
        <div className="flex flex-wrap items-center gap-x-10 gap-y-4">
          {CERTS.map((c) => (
            <span
              key={c}
              className="font-heading text-2xl md:text-3xl text-olive/80 tracking-tight"
              data-testid={`cert-${c.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {c}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactCTA() {
  return (
    <section
      className="relative overflow-hidden"
      data-testid="home-contact-cta"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[60vh]">
        <div className="lg:col-span-7 bg-[#1C2B1A] text-bone p-10 lg:p-20 flex flex-col justify-center">
          <span className="overline text-[#C9A84C]">10 — Let's Build Together</span>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl mt-6 leading-[1.05]">
            Ready to source dairy with{" "}
            <span className="italic text-[#C9A84C]">batch-level certainty?</span>
          </h2>
          <p className="mt-8 text-bone/85 max-w-xl leading-relaxed">
            Whether you procure for a 200-property hotel group, a private label
            export portfolio, or an institutional kitchen — we're built for the
            volume and the spec.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-bone text-olive-deep px-7 py-4 text-xs font-medium uppercase tracking-widest2 hover:bg-[#C9A84C] hover:text-[#1A1A1A] transition-colors"
              data-testid="home-cta-contact"
            >
              Start an Inquiry
              <ArrowUpRight size={14} weight="bold" />
            </Link>
            <Link
              to="/process"
              className="inline-flex items-center gap-2 border border-bone/40 text-bone px-7 py-4 text-xs font-medium uppercase tracking-widest2 hover:bg-bone hover:text-[#1C2B1A] transition-colors"
              data-testid="home-cta-process"
            >
              View The Process
              <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
        <div className="lg:col-span-5 relative min-h-[320px]">
          <img
            src={IMAGES.herd}
            alt="AxonPremium owned herd — premium dairy farm"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
      </div>
    </section>
  );
}
